import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import { BrowserRouter, Route, Routes, useNavigate, useSearchParams } from 'react-router-dom';

import CustomToaster from '@components/CustomToaster';
import { AppContextProvider, Env } from '@contexts/AppContext';
import { useScrollToTop } from '@hooks';
import useCurrentUser from '@hooks/useCurrentUser';
import { STRIPE_PUBLISH_KEY } from '@src/env';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { User } from '@utils/backend-api/currentUser';
import { EMAIL_MESSAGE_CONFIRMATION } from '@utils/constants';

import EmailConfirmation from '../EmailConfirmation';
import CommunityInsights from '../pages/CommunityInsights';
import EditForumPostPage from '../pages/Forum/EditForumPostPage';
import ForumIndexPage from '../pages/Forum/ForumIndexPage';
import ForumLayout from '../pages/Forum/ForumLayout';
import ForumPostPage from '../pages/Forum/ForumPostPage';
import Partners from '../pages/Partners';
import PaymentStatus from '../pages/PaymentStatus';
import Pro from '../pages/Pro';
import SettingsPage from '../pages/Settings/SettingsPage';
import Account from '../pages/Settings/SettingsPage/Account';
import Affiliate from '../pages/Settings/SettingsPage/Affiliate';
import EmailPreferences from '../pages/Settings/SettingsPage/EmailPreferences';
import Gift from '../pages/Settings/SettingsPage/Gift';
import Privacy from '../pages/Settings/SettingsPage/Privacy';
import Team from '../pages/Settings/SettingsPage/Team';
import EditTopicsSettings from '../pages/Settings/UserProfile/EditTopicsSettings';
import SubscriberDirectory from '../pages/SubscriberDirectory';
import SubscriberProfile from '../pages/SubscriberProfile';
import UpdatePayment from '../pages/UpdatePayment';
import MetaTagLayout from './MetaTagLayout';
import ProtectedRoute from './ProtectedRoute';

const queryClient = new QueryClient();
const stripePromise = loadStripe(STRIPE_PUBLISH_KEY);

function AppRoutes() {
  useScrollToTop();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();

  // Set the rc param on every location change
  useEffect(() => {
    if (currentUser?.affiliate_code && !params.get('rc')) {
      params.set('rc', currentUser.affiliate_code);
      navigate(`${location.pathname}?${params.toString()}`, { replace: true });
    }
  }, [params, currentUser?.affiliate_code, navigate]);

  return (
    <Routes>
      <Route element={<MetaTagLayout />}>
        <Route element={<ForumLayout />}>
          <Route path="/forum" element={<ForumIndexPage />} />
          <Route path="/forum/recent" element={<ForumIndexPage order="most_recent" />} />
          <Route path="/forum/comments" element={<ForumIndexPage order="most_comments" />} />
          <Route path="/forum/posts/:id" element={<ForumPostPage />} />
          <Route path="/forum/posts/:id/edit" element={<EditForumPostPage />} />
          <Route path="/forum/posts/new" element={<EditForumPostPage />} />
        </Route>
        <Route path="/partners" element={<Partners />} />
        <Route path="/pro" element={<Pro />} />
        <Route path="/insights" element={<CommunityInsights />} />
        <Route path="/directory" element={<SubscriberDirectory />} />
        <Route path="/u/:username" element={<SubscriberProfile />} />
        <Route path="/u/:username/:article_page_id" element={<SubscriberProfile />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/payment-status" element={<PaymentStatus />} />
          <Route path="/subscription/update-payment" element={<UpdatePayment />} />
          <Route element={<SettingsPage />}>
            <Route path="/unsubscribe" element={<EmailPreferences />} />
          </Route>
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<Account />} />
            <Route path="email-preferences" element={<EmailPreferences />} />
            <Route path="affiliate-settings" element={<Affiliate />} />
            <Route path="gift-subscription" element={<Gift />} />
            <Route path="team-settings" element={<Team />} />
            <Route path="privacy-settings" element={<Privacy />} />
            <Route path="topics" element={<EditTopicsSettings />} />
          </Route>
        </Route>
        <Route
          path="/email-confirmation"
          element={<EmailConfirmation message={EMAIL_MESSAGE_CONFIRMATION} />}
        />
      </Route>
    </Routes>
  );
}

const App = ({
  env,
  currentUser,
  isWebview,
}: {
  env: Env;
  currentUser: User;
  isWebview: boolean;
}) => {
  return (
    <AppContextProvider env={env} initialCurrentUser={currentUser} isWebview={isWebview}>
      <QueryClientProvider client={queryClient}>
        <Elements stripe={stripePromise}>
          <div className="App">
            <BrowserRouter>
              <AppRoutes />
              <CustomToaster />
            </BrowserRouter>
          </div>
        </Elements>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default App;
